// frontend/src/components/admin/SystemConfig.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './SystemConfig.css';

const SystemConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    registration_reward: 0,
    article_read_reward: 0,
    max_wallet_holders: 0,
    min_withdrawal_amount: 10,
    withdrawal_enabled: true,
    withdrawal_allowed_countries: 'US'
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await api.get(
        '/admin/config',
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setConfig(response.data);
      setEditForm({
        registration_reward: response.data.registration_reward,
        article_read_reward: response.data.article_read_reward,
        max_wallet_holders: response.data.max_wallet_holders,
        min_withdrawal_amount: response.data.min_withdrawal_amount ?? 10,
        withdrawal_enabled: response.data.withdrawal_enabled ?? true,
        withdrawal_allowed_countries: response.data.withdrawal_allowed_countries ?? 'US'
      });
      setError('');
    } catch (err) {
      console.error('Failed to fetch config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!window.confirm('Update system configuration? Server restart may be required for changes to take effect.')) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('auth_token');

      console.log('Sending config update:', editForm);
      console.log('API base URL:', api.defaults?.baseURL);
      console.log('Full URL will be:', api.defaults?.baseURL + '/admin/config');

      const response = await api.put(
        '/admin/config',
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Config update SUCCESS:', response.data);

      // Only show success if we get here (no errors thrown)
      alert(`✅ Configuration updated successfully!\n\nUpdated values:\n${JSON.stringify(response.data.updated, null, 2)}\n\n⚠️ Please restart the server for changes to take effect.`);

      // Refresh config to show new values
      await fetchConfig();
    } catch (err) {
      console.error('❌ Config update FAILED:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });

      // Show detailed error to user
      let errorMsg = 'Failed to update configuration';

      if (err.response?.status === 404) {
        errorMsg = '404 Not Found - The /admin/config endpoint is not available. Check that admin_routes.py is loaded.';
      } else if (err.response?.status === 403) {
        errorMsg = '403 Forbidden - You do not have admin access';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }

      alert(`❌ Error: ${errorMsg}\n\nCheck browser console for details.`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (config) {
      setEditForm({
        registration_reward: config.registration_reward,
        article_read_reward: config.article_read_reward,
        max_wallet_holders: config.max_wallet_holders,
        min_withdrawal_amount: config.min_withdrawal_amount ?? 10,
        withdrawal_enabled: config.withdrawal_enabled ?? true,
        withdrawal_allowed_countries: config.withdrawal_allowed_countries ?? 'US'
      });
    }
  };

  if (loading) {
    return (
      <div className="system-config-loading">
        <div className="spinner"></div>
        <p>Loading configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="system-config-error">
        <p>❌ {error}</p>
        <button onClick={fetchConfig} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  const walletPercentage = ((config.current_wallet_count / config.max_wallet_holders) * 100).toFixed(1);
  const hasChanges =
    editForm.registration_reward !== config.registration_reward ||
    editForm.article_read_reward !== config.article_read_reward ||
    editForm.max_wallet_holders !== config.max_wallet_holders ||
    editForm.min_withdrawal_amount !== (config.min_withdrawal_amount ?? 10) ||
    editForm.withdrawal_enabled !== (config.withdrawal_enabled ?? true) ||
    editForm.withdrawal_allowed_countries !== (config.withdrawal_allowed_countries ?? 'US');

  return (
    <div className="system-config">

      {/* Header */}
      <div className="config-header">
        <h2>⚙️ System Configuration</h2>
        <div className="header-badge">
          <span className="super-admin-badge">SUPER ADMIN ONLY</span>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="warning-banner">
        <span className="warning-icon">⚠️</span>
        <div className="warning-content">
          <strong>Important:</strong> Changes to these settings require a server restart to take effect.
          Make sure to coordinate with your deployment process.
        </div>
      </div>

      {/* Current Configuration Overview */}
      <div className="config-overview">
        <h3>📊 Current Configuration</h3>
        <div className="overview-grid">

          <div className="overview-card">
            <div className="overview-icon">🎁</div>
            <div className="overview-content">
              <h4>Registration Reward</h4>
              <p className="overview-value">{config.registration_reward} DOGE</p>
              <p className="overview-label">Per new user signup</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">📰</div>
            <div className="overview-content">
              <h4>Article Read Reward</h4>
              <p className="overview-value">{config.article_read_reward} DOGE</p>
              <p className="overview-label">Per article read</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">💛</div>
            <div className="overview-content">
              <h4>Wallet Limit</h4>
              <p className="overview-value">{config.max_wallet_holders}</p>
              <p className="overview-label">Maximum wallet holders</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">📈</div>
            <div className="overview-content">
              <h4>Current Usage</h4>
              <p className="overview-value">{config.current_wallet_count} / {config.max_wallet_holders}</p>
              <p className="overview-label">{walletPercentage}% filled</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">💸</div>
            <div className="overview-content">
              <h4>Min Withdrawal</h4>
              <p className="overview-value">{config.min_withdrawal_amount ?? 10} DOGE</p>
              <p className="overview-label">Minimum to withdraw</p>
            </div>
          </div>

          <div className="overview-card">
            <div className="overview-icon">{(config.withdrawal_enabled ?? true) ? '✅' : '🔴'}</div>
            <div className="overview-content">
              <h4>Withdrawals</h4>
              <p className="overview-value" style={{color: (config.withdrawal_enabled ?? true) ? '#4caf50' : '#f44336'}}>
                {(config.withdrawal_enabled ?? true) ? 'Enabled' : 'Disabled'}
              </p>
              <p className="overview-label">Current status</p>
            </div>
          </div>

        </div>
      </div>

      {/* Edit Form */}
      <div className="config-edit-section">
        <h3>✏️ Edit Configuration</h3>

        <div className="config-form">

          {/* Registration Reward */}
          <div className="form-group">
            <label>
              <span className="label-icon">🎁</span>
              Registration Reward (DOGE)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={editForm.registration_reward}
              onChange={(e) => setEditForm({
                ...editForm,
                registration_reward: parseFloat(e.target.value)
              })}
            />
            <small className="form-help">
              Amount of DOGE rewarded when a new user registers with a wallet
            </small>
          </div>

          {/* Article Read Reward */}
          <div className="form-group">
            <label>
              <span className="label-icon">📰</span>
              Article Read Reward (DOGE)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={editForm.article_read_reward}
              onChange={(e) => setEditForm({
                ...editForm,
                article_read_reward: parseFloat(e.target.value)
              })}
            />
            <small className="form-help">
              Amount of DOGE rewarded when a user reads an article
            </small>
          </div>

          {/* Max Wallet Holders */}
          <div className="form-group">
            <label>
              <span className="label-icon">💛</span>
              Maximum Wallet Holders
            </label>
            <input
              type="number"
              min="0"
              value={editForm.max_wallet_holders}
              onChange={(e) => setEditForm({
                ...editForm,
                max_wallet_holders: parseInt(e.target.value) || 0
              })}
            />
            <small className="form-help">
              Maximum number of users who can connect wallets (currently: {config.current_wallet_count})
            </small>
          </div>

          {/* Min Withdrawal Amount */}
          <div className="form-group">
            <label>
              <span className="label-icon">💸</span>
              Minimum Withdrawal Amount (DOGE)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={editForm.min_withdrawal_amount}
              onChange={(e) => setEditForm({
                ...editForm,
                min_withdrawal_amount: parseFloat(e.target.value) || 0
              })}
            />
            <small className="form-help">
              Users must have at least this much DOGE before they can request a withdrawal
            </small>
          </div>

          {/* Withdrawal Enabled Toggle */}
          <div className="form-group">
            <label>
              <span className="label-icon">🔓</span>
              Withdrawals Enabled
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={editForm.withdrawal_enabled}
                  onChange={(e) => setEditForm({ ...editForm, withdrawal_enabled: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ color: editForm.withdrawal_enabled ? '#4caf50' : '#f44336', fontWeight: '600' }}>
                  {editForm.withdrawal_enabled ? '✅ Withdrawals are ON' : '🔴 Withdrawals are OFF'}
                </span>
              </label>
            </div>
            <small className="form-help">
              Disable to block all withdrawal requests without any code changes
            </small>
          </div>

          {/* Allowed Countries */}
          <div className="form-group">
            <label>
              <span className="label-icon">🌍</span>
              Allowed Withdrawal Countries
            </label>
            <input
              type="text"
              value={editForm.withdrawal_allowed_countries}
              onChange={(e) => setEditForm({ ...editForm, withdrawal_allowed_countries: e.target.value.toUpperCase() })}
              placeholder="US,CA,GB,AU"
            />
            <small className="form-help">
              Comma-separated ISO country codes (e.g. US,CA,GB). Users outside these countries cannot withdraw.
            </small>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              className="btn-reset"
              onClick={handleReset}
              disabled={!hasChanges || saving}
            >
              ↺ Reset Changes
            </button>
            <button
              className="btn-save"
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? '💾 Saving...' : '💾 Save Configuration'}
            </button>
          </div>

        </div>
      </div>

      {/* System Information */}
      <div className="system-info-section">
        <h3>ℹ️ System Information</h3>
        <div className="info-grid">

          <div className="info-item">
            <span className="info-label">Super Admin Email:</span>
            <span className="info-value">{config.super_admin_email}</span>
          </div>

          <div className="info-item">
            <span className="info-label">Configuration File:</span>
            <span className="info-value">/home/ec2-user/focusview-api/.env</span>
          </div>

          <div className="info-item">
            <span className="info-label">Last Updated:</span>
            <span className="info-value">{new Date().toLocaleString()}</span>
          </div>

        </div>
      </div>

      {/* Restart Instructions */}
      <div className="restart-instructions">
        <h4>🔄 How to Apply Changes</h4>
        <ol>
          <li>Click "Save Configuration" to update the .env file</li>
          <li>SSH into your EC2 server</li>
          <li>Navigate to the API directory: <code>cd /home/ec2-user/focusview-api</code></li>
          <li>Restart the service: <code>sudo systemctl restart focusview-api</code></li>
          <li>Verify the service: <code>sudo systemctl status focusview-api</code></li>
        </ol>
      </div>

    </div>
  );
};

export default SystemConfig;